import React, { useCallback, useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { getCurrentDomain, getUrlParam, isEmpty } from '../../../helpers/common';
import {
  addFileData,
  addProjectResource,
  getCurrentProjectDetails,
  getProjectFiles,
  updateProjectResource
} from '../../../services/projectServices';
import { captureException } from '../../../services/logService';
import { LinkModalInterface, ProjectFileData } from '../../../interfaces/ProjectInterface';
import { BUTTON_TYPE, FILE_TYPES, RESOURCE_TYPE } from '../../../global/constants';
import { DropboxModal, ModalButton, ModalContent, ModalForm } from './styles';
import Button from '../../button';
import {
  addTaskResource,
  dispatchTaskError,
  dispatchTaskSuccess,
  getRawTaskLinks,
  updateTaskResource
} from '../../../services/taskServices';
import { TaskLinkList } from '../../../interfaces/TaskInterface';
import { rootStore } from '../../../mobx/rootStore';
import { REACT_APP_DROPBOX_APP_ID } from '../../../global/environment';
import { observer } from 'mobx-react-lite';
import ModalHeader from '../modalHeader';
import Input from '../../input';

interface Props {
  onClose: () => void;
  modalData: LinkModalInterface | undefined;
  loadFilesData?: () => void;
  accounting?: boolean;
  itemData?: ProjectFileData | null;
  taskId?: string;
}
const LinkFileModal: React.FC<Props> = (props) => {
  const { onClose, modalData, loadFilesData, accounting, itemData, taskId } = props;
  //States
  const [name, setName] = useState(itemData?.name || '');
  const [url, setUrl] = useState(itemData?.url || '');
  const [loading, setLoading] = useState(false);

  //Other variable
  const params: { id: string } = useParams();
  const history = useHistory();
  const folderId = getUrlParam(history.location.search, 'folder');

  //Mobx store variable
  const { projectStore } = rootStore;
  const { currentProject } = projectStore;

  const onCreateFile = useCallback(
    async (e: React.SyntheticEvent) => {
      try {
        e.preventDefault();
        setLoading(true);
        if (isEmpty(name) && accounting) {
          dispatchTaskError('Please enter file name!');
          return;
        } else if (isEmpty(url)) {
          dispatchTaskError('Please enter valid URL!');
          return;
        }

        let response: TaskLinkList;
        if (accounting) {
          const payload = {
            FileType: null,
            IsSystemFile: true,
            Name: name,
            ParentId: folderId ? folderId : null,
            ProjectId: params?.id,
            Size: null,
            UploadFileType: modalData?.modalType,
            Url: url
          };
          response = await addFileData(payload);
        } else {
          if (itemData?.id) {
            const payload = {
              label: name,
              reference: !url.startsWith('https://') && !url.startsWith('http://') ? `https://${url.trim()}` : url
            };
            response = await (taskId
              ? updateTaskResource(taskId, itemData?.id, payload)
              : updateProjectResource(currentProject?.id, itemData?.id, payload));
            if (response) {
              const link = getRawTaskLinks(response);
              rootStore.taskStore.currentTask?.updateLink(itemData?.id, link);
              if (!taskId) {
                currentProject?.updateResource(itemData?.id, { label: name, reference: url });
              }
            }
          } else {
            const payload: {
              label?: string;
              reference: string;
              type?: number;
            } = {
              label: name,
              reference: !url.startsWith('https://') && !url.startsWith('http://') ? `https://${url.trim()}` : url
            };
            if (taskId) {
              response = await addTaskResource(taskId, payload);
            } else {
              payload.type = RESOURCE_TYPE.LINK;
              response = await addProjectResource(currentProject?.id, payload);
            }
          }
        }
        if (response) {
          onClose();
          setName('');
          setUrl('');
          if (accounting) {
            loadFilesData?.();
            await getProjectFiles(params?.id);
          } else {
            if (itemData?.id) {
              dispatchTaskSuccess('Link updated');
            } else {
              if (taskId) {
                dispatchTaskSuccess('Link added successfully');
                const link = getRawTaskLinks(response);
                rootStore.taskStore.currentTask?.addLink(link);
              } else {
                await getCurrentProjectDetails(currentProject?.id);
              }
            }
          }
        }
      } catch (e) {
        captureException(e);
      } finally {
        setLoading(false);
      }
    },
    [
      name,
      accounting,
      url,
      folderId,
      params?.id,
      modalData?.modalType,
      itemData?.id,
      taskId,
      currentProject,
      onClose,
      loadFilesData
    ]
  );

  const getButtonText = useCallback(() => {
    if (modalData?.modalType === FILE_TYPES.ADD_LINK && !itemData) return 'Add link';
    if (modalData?.modalType === FILE_TYPES.ADD_LINK && itemData?.id) return 'Save';
    if (itemData?.id) return 'Update';
    return 'Save';
  }, [itemData, modalData?.modalType]);

  //Dropbox related code bellow

  /**
   * Loads Dropbox SDK script dynamically when needed for file picker functionality.
   * Only loads if script isn't already present and modal type is DROPBOX.
   * Sets app key from environment variables for Dropbox authentication.
   */
  useEffect(() => {
    const hostName = getCurrentDomain();
    if (hostName !== 'dash.teamcamp.app' && hostName !== 'beta.teamcamp.app' && hostName !== 'localhost') return;
    // Only load the script if it's not already in the DOM
    if (!document.getElementById('dropboxjs') && modalData?.modalType === FILE_TYPES.DROPBOX) {
      const script = document.createElement('script');
      script.src = 'https://www.dropbox.com/static/api/2/dropins.js';
      script.id = 'dropboxjs';
      script.setAttribute('data-app-key', REACT_APP_DROPBOX_APP_ID || '');
      document.head.appendChild(script);
    }

    return () => {
      const script = document.getElementById('dropboxjs');
      if (script && modalData?.modalType === FILE_TYPES.DROPBOX) {
        script.remove();
      }
    };
  }, [modalData?.modalType]);

  // Define Dropbox interfaces
  interface DropboxFile {
    name: string;
    link: string;
  }
  // Extends Window interface to include Dropbox SDK types and file picker functionality
  interface DropboxWindow extends Window {
    Dropbox?: {
      choose: (options: {
        success: (files: DropboxFile[]) => void;
        cancel: () => void;
        linkType: 'preview' | 'direct';
        multiselect: boolean;
        folderselect: boolean;
      }) => void;
    };
  }

  /**
   * Handles the click event for the Dropbox chooser
   * Opens the Dropbox file picker dialog when clicked
   * On successful file selection:
   * - Sets the name state to the selected file's name
   * - Sets the url state to the selected file's link
   * Only allows single file selection and no folder selection
   * Shows preview links rather than direct download links
   */
  const onClickDropbox = useCallback(() => {
    const hostName = getCurrentDomain();
    if (hostName !== 'dash.teamcamp.app' && hostName !== 'beta.teamcamp.app' && hostName !== 'localhost') {
      window.open(modalData?.href, '_blank');
    } else {
      const dropboxWindow = window as DropboxWindow;
      if (dropboxWindow.Dropbox) {
        dropboxWindow.Dropbox.choose({
          success: (files: DropboxFile[]) => {
            if (files && files.length > 0) {
              setName(files[0].name);
              setUrl(files[0].link);
            }
          },
          cancel: () => {
            setName('');
            setUrl('');
          },
          linkType: 'preview',
          multiselect: false,
          folderselect: false
        });
      } else {
        console.error('Dropbox SDK not loaded');
      }
    }
  }, [modalData?.href]);

  const Label =
    modalData?.modalType === FILE_TYPES.DROPBOX ? (
      <div role='button' tabIndex={0} onClick={onClickDropbox}>
        Browse Dropbox
      </div>
    ) : (
      modalData?.modalType !== FILE_TYPES.NOTION &&
      modalData?.modalType !== FILE_TYPES.RESOURCES &&
      modalData?.modalType !== FILE_TYPES.ADD_LINK && (
        <a
          href={modalData?.href}
          rel={modalData?.modalType === FILE_TYPES.DROPBOX ? 'noopener noreferrer' : 'noreferrer'}
          target='_blank'>
          Browse {modalData?.modalType !== FILE_TYPES.NOTION ? modalData?.modal : ''}
        </a>
      )
    );

  return (
    <>
      <DropboxModal>
        <ModalHeader
          iconName={modalData?.name}
          iconViewBox={
            modalData?.modalType === FILE_TYPES.DRIVE ||
            modalData?.modalType === FILE_TYPES.DROPBOX ||
            modalData?.modalType === FILE_TYPES.FIGMA
              ? '0 0 20 20'
              : '0 0 18 18'
          }
          iconFill={
            modalData?.modalType === FILE_TYPES.RESOURCES ||
            modalData?.modalType === FILE_TYPES.ADD_LINK ||
            modalData?.modalType === FILE_TYPES.NOTION
              ? 'var(--text-primary)'
              : ''
          }
          title={itemData?.id ? modalData?.updateTitle : modalData?.title}
          onClose={onClose}
        />
        <ModalContent>
          <ModalForm onSubmit={onCreateFile}>
            <Input
              placeHolder={modalData?.placeholder}
              value={name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
              autoFocus
            />
            <Input
              label={
                <>
                  {modalData?.label} {Label}
                </>
              }
              placeHolder={modalData?.placeholder2}
              value={url}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUrl(e.target.value)}
              className='input-field'
            />
            <ModalButton>
              <Button title='Cancel' onClick={onClose} variant={BUTTON_TYPE.SECONDARY} buttonType='button' />
              <Button variant={BUTTON_TYPE.PRIMARY} title={getButtonText()} isLoading={loading} buttonType='submit' />
            </ModalButton>
          </ModalForm>
        </ModalContent>
      </DropboxModal>
    </>
  );
};

export default observer(LinkFileModal);
